import { useRef, useState, type KeyboardEvent } from 'react'
import { CalendarIcon, ChevronDown } from 'lucide-react'

import { useOrganizationUsers } from '@/stores/organization.store'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Textarea } from '@/components/ui/textarea'
import { UserAvatar } from '@/components/shared/user-avatar'
import { jobTaskDueLabel, jobTaskDuePicks, startOfLocalDay } from '@/components/pipeline/job-detail-view'

export interface JobTaskDraft {
  title: string
  description: string
  assignedTo: string
  dueDate: Date
}

interface JobTaskComposerProps {
  /** Default assignee. */
  currentUserId?: string
  /** Earliest event day, for the event-relative due picks. */
  eventDay?: Date
  isPending: boolean
  /** Resolves once the task exists (the composer then resets and stays open); rejects to keep the draft. */
  onCreate: (draft: JobTaskDraft) => Promise<void>
  onOpenFullForm: (draft: JobTaskDraft) => void
  onCancel: () => void
}

/** Inline "new task" form at the bottom of the Open tasks card. */
export function JobTaskComposer({
  currentUserId,
  eventDay,
  isPending,
  onCreate,
  onOpenFullForm,
  onCancel,
}: JobTaskComposerProps) {
  const { users } = useOrganizationUsers()
  const titleRef = useRef<HTMLInputElement>(null)
  const [now] = useState(() => new Date())
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [showDescription, setShowDescription] = useState(false)
  const [assigneeOverride, setAssigneeOverride] = useState<string>()
  const [dueOverride, setDueOverride] = useState<Date>()
  const [assigneeOpen, setAssigneeOpen] = useState(false)
  const [dueOpen, setDueOpen] = useState(false)

  const assignedTo = assigneeOverride ?? currentUserId ?? ''
  const dueDate = dueOverride ?? startOfLocalDay(now)
  const assignee = users.find((orgUser) => orgUser.user?.id === assignedTo)?.user
  const canCreate = title.trim().length > 0 && assignedTo !== '' && !isPending

  const draft = (): JobTaskDraft => ({ title: title.trim(), description: description.trim(), assignedTo, dueDate })

  async function create() {
    if (!canCreate) return
    try {
      await onCreate(draft())
    } catch {
      // The caller reported the failure; keep the draft so the user can retry.
      return
    }
    setTitle('')
    setDescription('')
    setShowDescription(false)
    setAssigneeOverride(undefined)
    setDueOverride(undefined)
    titleRef.current?.focus()
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      event.preventDefault()
      void create()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      onCancel()
    }
  }

  return (
    <div className="space-y-2.5 rounded-md border p-3">
      <Input
        ref={titleRef}
        autoFocus
        value={title}
        disabled={isPending}
        onChange={(event) => setTitle(event.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Task title"
        aria-label="New task title"
        className="h-8"
      />

      {showDescription ? (
        <Textarea
          value={description}
          disabled={isPending}
          onChange={(event) => setDescription(event.target.value)}
          placeholder="Description"
          aria-label="New task description"
          rows={3}
        />
      ) : (
        <button
          type="button"
          onClick={() => setShowDescription(true)}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          + Add description
        </button>
      )}

      <div className="flex flex-wrap gap-1.5">
        <Popover open={assigneeOpen} onOpenChange={setAssigneeOpen}>
          <PopoverTrigger asChild>
            <Button type="button" variant="outline" size="sm" aria-label="Assignee" className="h-7 gap-1.5 px-2 text-xs">
              <UserAvatar firstName={assignee?.firstName} lastName={assignee?.lastName} size="sm" className="h-4 w-4 text-[8px]" />
              {assignee?.firstName || 'Assign'}
              <ChevronDown className="h-3 w-3 text-muted-foreground" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-48 p-1">
            {users.map((orgUser) => (
              <button
                key={orgUser.user?.id}
                type="button"
                onClick={() => {
                  setAssigneeOverride(orgUser.user?.id)
                  setAssigneeOpen(false)
                }}
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-muted"
              >
                <UserAvatar firstName={orgUser.user?.firstName} lastName={orgUser.user?.lastName} size="sm" />
                {orgUser.user?.firstName} {orgUser.user?.lastName}
              </button>
            ))}
          </PopoverContent>
        </Popover>

        <Popover open={dueOpen} onOpenChange={setDueOpen}>
          <PopoverTrigger asChild>
            <Button type="button" variant="outline" size="sm" aria-label="Due date" className="h-7 gap-1.5 px-2 text-xs">
              <CalendarIcon className="h-3 w-3" />
              {jobTaskDueLabel(dueDate, now)}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-auto p-1">
            <div className="flex flex-col">
              {jobTaskDuePicks(now, eventDay).map((pick) => (
                <button
                  key={pick.label}
                  type="button"
                  onClick={() => {
                    setDueOverride(pick.date)
                    setDueOpen(false)
                  }}
                  className="rounded-sm px-2 py-1.5 text-left text-sm hover:bg-muted"
                >
                  {pick.label}
                </button>
              ))}
            </div>
            <div className="border-t">
              <Calendar
                mode="single"
                selected={dueDate}
                onSelect={(date) => {
                  if (!date) return
                  setDueOverride(startOfLocalDay(date))
                  setDueOpen(false)
                }}
              />
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => onOpenFullForm(draft())}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          Open full form
        </button>
        <div className="flex items-center gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="button" size="sm" disabled={!canCreate} onClick={() => void create()}>
            Create
          </Button>
        </div>
      </div>
    </div>
  )
}
